/////////////////////////////////////////////////////////////////////
// Per-variation stock.                                             //
//                                                                  //
// util/productVariants.js turns a brand's size/volume/scent/colour  //
// fields into dropdowns. A brand rarely holds the same number of    //
// every version though: they might have five Small Red but only one //
// Large Blue. This module stores a count per *combination* of those //
// choices and answers the two questions the rest of the app asks:   //
// "how many of exactly this version are left?" and "which options   //
// should be greyed out?".                                           //
//                                                                  //
// Where the counts live                                            //
// ----------------------                                           //
// Sharetribe's stock API holds a single number per listing, so the  //
// per-combination counts are kept in the listing's publicData under //
// `variantStock`, keyed by getVariantCombinationKey. The listing's  //
// real (API) stock is kept as the *sum* of those counts, which is   //
// what keeps Sharetribe's own reservation and oversell protection   //
// meaningful: a listing whose variations all hit zero has a total   //
// of zero and stops being orderable everywhere, including in places //
// that know nothing about variants.                                 //
//                                                                   //
// Two totals, one source of truth                                   //
// -------------------------------                                   //
// The two can drift — most obviously because Sharetribe decrements  //
// the listing total when an order is placed but knows nothing about //
// the map. getVariantStockSummary() reports that drift so the       //
// vendor can be told about it; see the note on decrementVariantStock //
// for where the automatic fix has to live.                          //
/////////////////////////////////////////////////////////////////////

import {
  getVariantCombinationKey,
  getVariantSelectionEntries,
  formatVariantSummary,
} from './productVariants';

/** publicData key the counts are stored under. */
export const VARIANT_STOCK_KEY = 'variantStock';

// Three fields with five options each is already 125 inputs to fill in. Past
// this the grid stops being something a vendor can reasonably manage, so the
// listing falls back to a single total and the panel says so.
export const MAX_VARIANT_COMBINATIONS = 200;

// Combination labels read "Small · Red" rather than "Size: Small · Colour: Red";
// the column the count sits in already says which field is which.
const COMBINATION_LABEL_SEPARATOR = ' · ';

/**
 * How many combinations a set of variant fields produces, without building them.
 *
 * @param {Array} variantFields as returned by getProductVariantFields
 * @returns {number} the number of combinations (0 when there are no fields)
 */
export const getVariantCombinationCount = (variantFields = []) =>
  variantFields.length === 0
    ? 0
    : variantFields.reduce((count, field) => count * (field.options?.length || 0), 1);

/**
 * Can this listing's variants be given individual stock counts?
 *
 * @param {Array} variantFields as returned by getProductVariantFields
 * @returns {boolean} false when there are no variants, or too many to manage
 */
export const supportsVariantStock = (variantFields = []) => {
  const count = getVariantCombinationCount(variantFields);
  return count > 0 && count <= MAX_VARIANT_COMBINATIONS;
};

/**
 * Every combination of the listing's variants, e.g. Small/Red, Small/Blue,
 * Medium/Red, … Fields vary in the order they are configured in, so the first
 * field changes slowest and the grid reads like a table.
 *
 * @param {Array} variantFields as returned by getProductVariantFields
 * @returns {Array<Object>} [{ key, label, entries }] — empty when unsupported
 */
export const getVariantCombinations = (variantFields = []) => {
  if (!supportsVariantStock(variantFields)) {
    return [];
  }

  const combinations = variantFields.reduce(
    (acc, field) =>
      acc.flatMap(entries =>
        field.options.map(option =>
          entries.concat({
            key: field.key,
            label: field.label,
            value: option.value,
            valueLabel: option.label,
          })
        )
      ),
    [[]]
  );

  return combinations.map(entries => ({
    key: getVariantCombinationKey(entries),
    label: entries.map(entry => entry.valueLabel || entry.value).join(COMBINATION_LABEL_SEPARATOR),
    // The fully qualified "Size: Small · Colour: Red" wording, for places with
    // no column headings to lean on (screen readers, the cart, emails).
    summary: formatVariantSummary(entries),
    entries,
  }));
};

/**
 * Read the stored counts, ignoring anything that isn't a whole number of items.
 *
 * @param {Object} publicData listing.attributes.publicData
 * @returns {Object} { [combinationKey]: count }
 */
export const getVariantStockMap = publicData => {
  const raw = publicData?.[VARIANT_STOCK_KEY];
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    return {};
  }
  return Object.keys(raw).reduce((acc, key) => {
    const count = Number.parseInt(raw[key], 10);
    return Number.isInteger(count) && count >= 0 ? { ...acc, [key]: count } : acc;
  }, {});
};

/**
 * Is this listing tracking stock per variation at all? Listings created before
 * the feature existed (and single-version products) are not, and keep using the
 * listing's own stock number.
 *
 * @param {Object} stockMap as returned by getVariantStockMap
 * @returns {boolean} true when there is at least one count to honour
 */
export const hasVariantStock = stockMap => Object.keys(stockMap || {}).length > 0;

/**
 * The count stored for one combination.
 *
 * Once a listing tracks variations at all, a combination that isn't in the map
 * is treated as sold out rather than unlimited: that is the state a listing is
 * left in when a vendor adds a new colour in the Details tab without giving it
 * a count, and guessing high there would oversell.
 *
 * @param {Object} stockMap as returned by getVariantStockMap
 * @param {string} combinationKey as returned by getVariantCombinationKey
 * @returns {number|null} the count, or null when the listing isn't tracking
 */
export const getCombinationStock = (stockMap, combinationKey) => {
  if (!hasVariantStock(stockMap)) {
    return null;
  }
  const count = stockMap[combinationKey];
  return Number.isInteger(count) && count >= 0 ? count : 0;
};

/**
 * The total across the combinations the listing currently offers. Counts left
 * behind by options the vendor has since removed don't count towards it — they
 * can't be bought, so including them would advertise stock that isn't there.
 *
 * @param {Object} stockMap as returned by getVariantStockMap
 * @param {Array} combinations as returned by getVariantCombinations
 * @returns {number} the sum of the counts
 */
export const getTotalVariantStock = (stockMap, combinations = []) =>
  combinations.reduce((sum, combination) => sum + (stockMap?.[combination.key] || 0), 0);

/**
 * Drop counts for combinations the listing no longer offers.
 *
 * @param {Object} stockMap as returned by getVariantStockMap
 * @param {Array} combinations as returned by getVariantCombinations
 * @returns {Object} the pruned map
 */
export const pruneVariantStock = (stockMap, combinations = []) =>
  combinations.reduce(
    (acc, combination) =>
      Number.isInteger(stockMap?.[combination.key])
        ? { ...acc, [combination.key]: stockMap[combination.key] }
        : acc,
    {}
  );

/**
 * How many of the version the shopper has chosen are left.
 *
 * @param {Object} params
 * @param {Array} params.variantFields as returned by getProductVariantFields
 * @param {Object} params.stockMap as returned by getVariantStockMap
 * @param {Object} params.selection { [fieldKey]: optionValue }
 * @returns {number|null} the count, or null when the listing isn't tracking per
 *   variation or the shopper hasn't finished choosing
 */
export const getStockForSelection = params => {
  const { variantFields = [], stockMap, selection = {} } = params || {};
  if (!hasVariantStock(stockMap) || variantFields.length === 0) {
    return null;
  }
  const entries = getVariantSelectionEntries(variantFields, selection);
  return entries.length === variantFields.length
    ? getCombinationStock(stockMap, getVariantCombinationKey(entries))
    : null;
};

/**
 * Which options are still buyable, so the order form can grey out the rest.
 *
 * An option is offered when at least one in-stock combination contains it *and*
 * agrees with the choices already made in the other dropdowns. That is what
 * makes a shopper who picked "Red" see only the sizes that come in red, while
 * the colours stay fully selectable so they can change their mind.
 *
 * @param {Object} params
 * @param {Array} params.variantFields as returned by getProductVariantFields
 * @param {Object} params.stockMap as returned by getVariantStockMap
 * @param {Object} [params.selection] the choices made so far
 * @returns {Object} { [fieldKey]: { [optionValue]: boolean } } — empty when the
 *   listing isn't tracking per variation, i.e. everything stays selectable
 */
export const getVariantOptionAvailability = params => {
  const { variantFields = [], stockMap, selection = {} } = params || {};
  if (!hasVariantStock(stockMap) || !supportsVariantStock(variantFields)) {
    return {};
  }

  const inStock = getVariantCombinations(variantFields).filter(
    combination => getCombinationStock(stockMap, combination.key) > 0
  );

  return variantFields.reduce((acc, field) => {
    const optionAvailability = field.options.reduce((options, option) => {
      const isAvailable = inStock.some(combination =>
        combination.entries.every(entry =>
          entry.key === field.key
            ? entry.value === option.value
            : // Fields the shopper hasn't chosen yet don't constrain anything.
              selection?.[entry.key] == null || selection[entry.key] === entry.value
        )
      );
      return { ...options, [option.value]: isAvailable };
    }, {});
    return { ...acc, [field.key]: optionAvailability };
  }, {});
};

/**
 * Everything a caller needs to reason about one listing's variation stock,
 * derived in one place so the order form, the basket and the edit panel can't
 * disagree with each other.
 *
 * @param {Object} params
 * @param {Array} params.variantFields as returned by getProductVariantFields
 * @param {Object} params.publicData listing.attributes.publicData
 * @param {Object} [params.selection] the choices made so far
 * @param {number} [params.listingStock] listing.currentStock quantity
 * @returns {Object} the stock map, combinations, per-option availability, the
 *   count for the current selection, and whether the two totals agree
 */
export const getVariantStockSummary = params => {
  const { variantFields = [], publicData, selection = {}, listingStock } = params || {};
  const stockMap = getVariantStockMap(publicData);
  const combinations = getVariantCombinations(variantFields);
  const isTracked = hasVariantStock(stockMap) && combinations.length > 0;
  const totalVariantStock = getTotalVariantStock(stockMap, combinations);

  return {
    stockMap,
    combinations,
    isTracked,
    totalVariantStock,
    optionAvailability: getVariantOptionAvailability({ variantFields, stockMap, selection }),
    selectedStock: getStockForSelection({ variantFields, stockMap, selection }),
    // Sharetribe decrements the listing total on every order but never the map,
    // so a mismatch is normal after a sale and means the map is the stale one.
    isOutOfSyncWithListingStock:
      isTracked && typeof listingStock === 'number' && listingStock !== totalVariantStock,
  };
};

/**
 * Take `quantity` off one combination, e.g. once an order for it is confirmed.
 *
 * NOTE: nothing in the client app calls this on a purchase, and it can't: only
 * the listing's author may write to its publicData, and the shopper placing the
 * order obviously isn't them. Sharetribe decrements the *listing* total itself,
 * which is what stops the listing overselling overall; keeping the per-variation
 * map in step needs a server-side transaction hook using the Integration API.
 * This function is the piece that hook would call.
 *
 * @param {Object} stockMap as returned by getVariantStockMap
 * @param {string} combinationKey as returned by getVariantCombinationKey
 * @param {number} quantity how many were bought
 * @returns {Object} the updated map (never negative)
 */
export const decrementVariantStock = (stockMap, combinationKey, quantity = 1) => {
  if (!hasVariantStock(stockMap) || !combinationKey) {
    return stockMap || {};
  }
  const current = stockMap[combinationKey];
  if (!Number.isInteger(current)) {
    return stockMap;
  }
  return { ...stockMap, [combinationKey]: Math.max(0, current - Math.max(0, quantity)) };
};
